/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { Grid } from "@cd2/components"
import { useLocation } from "react-router-dom"
import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { Fields } from "@cd2/forms"
import { P } from "@cd2/text"
import { CGButton } from "./CGButton"
import { useApi } from "../providers/ApiProvider"
import { MultiFileUploader, IFileData } from "@cd2/fileboy-react"
import { useDialogueConfirmation2 } from "../hooks/useDialogConfirmation"
import { useMutatorState } from "../hooks/useMutatorState"
import { useAsyncCallback } from "../hooks/useAsyncCallback"
import { FieldWithDescription } from "./FieldWithDescription"
import { TextGroupMedium, TextGroupSmall } from "./Title"
import { ButtonGrid } from "../pages/employees/EmployeesTasksPage"
import { SaveButton } from "../pages/user/UserManageEmployeePage/UserManageEmployeeLoginCredentialsTab"
import { CancelButton } from "./CancelButton"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { useGlobalState } from "../utils/globalState"
import { IRecord } from "../types/schema"
import { PopoutInput } from "./PopoutInput"

export type IHelpModalStage = "choose" | "general"

export function useHelpRequestDialog() {
  return useDialogueConfirmation2<boolean>({
    Component({ onResolve, registerOutsideClick }) {
      registerOutsideClick(() => onResolve(false))
      const api = useApi()
      const siteAlerts = useSiteAlerts()
      const location = useLocation()
      const globalState = useGlobalState()

      const resultId = /case-coaching\/([a-zA-Z0-9-]*)\/results/.exec(location.pathname)?.[1]

      const [state, mutate] = useMutatorState({
        stage: (resultId ? "choose" : "general") as IHelpModalStage,
        sendTo: null as null | "cg" | { id: string; name: string },
        targetCompanies: [globalState.company] as IRecord<"companies", "name">[],
        message: "",
        files: [] as string[],
        isAboutResults: false,
        agreedToShareResults: false,
        errors: {} as { sendTo?: string; message?: string; agreedToShareResults?: string },
      })

      useAsyncEffect(async () => {
        if (globalState.company.isSubCompany) {
          const headCompany = await window.skeem.fetch2<IRecord<"companies", "name">>({
            first: {
              name: "companies",
              attributes: ["name"],
              filter: { matches: { subCompanies: { some: { id: globalState.companyId } } } },
            },
          })
          mutate.setProp("targetCompanies", [headCompany, globalState.company])
        }
      }, [])

      const handleFilesUploaded = (files: IFileData[]) => {
        mutate.setProp(
          "files",
          files.map(file => file.id),
        )
      }

      const handleCreateRequest = useAsyncCallback(async () => {
        const errors = {} as typeof state.errors
        if (state.sendTo === null) {
          errors.sendTo = "Please select a recipient"
        }
        if (state.message.trim() === "") {
          errors.message = "Please enter a message"
        }
        if (state.isAboutResults && !state.agreedToShareResults) {
          errors.agreedToShareResults = "You must agree to share your results"
        }

        mutate.setProp("errors", errors)
        if (Object.keys(errors).length > 0) return
        try {
          const helpRequest = await window.skeem.mutate({
            helpRequests: {
              create: {
                url: window.location.href,
                userAgent: window.navigator.userAgent,
                user: { set: { session: true } },
                targetCompany: {
                  set: typeof state.sendTo === "object" ? state.sendTo!.id : null,
                },
                flowResult: state.isAboutResults && resultId ? { set: resultId } : undefined,
                message: state.message.trim(),
                fileboyIds: state.files,
              },
            },
          })
          try {
            await api.sendHelpRequestEmail(helpRequest.id)
          } catch (e) {
            // FIXME: report this error
            console.error("Failed to send help request email", e)
          }
          onResolve(true)
          siteAlerts.genericSuccess("Your help request has been sent")
        } catch (e) {
          console.error(e)
          siteAlerts.genericError(
            "There was a problem sending your help request. Please try again later.",
          )
        }
      })

      return (
        <div>
          {state.stage === "choose" && (
            <Grid gap={16}>
              <TextGroupMedium
                heading="What do you need help with?"
                body="This will send a message to our support team, who will respond as soon as they can."
              />
              <Grid columns={[{ fr: 1 }, "auto", { fr: 1 }]} gap={8}>
                <CGButton
                  onClick={() => mutate.setPartial({ stage: "general", isAboutResults: true })}
                >
                  Help with my results
                </CGButton>
                <div
                  css={css`
                    align-self: center;
                    justify-self: center;
                  `}
                >
                  <P>or</P>
                </div>
                <CGButton onClick={() => mutate.setProp("stage", "general")}>
                  Help with something else
                </CGButton>
              </Grid>
              <ButtonGrid>
                <SaveButton onClick={() => {}} disabled />
                <CancelButton onClick={() => onResolve(false)} />
              </ButtonGrid>
            </Grid>
          )}
          {state.stage !== "choose" && (
            <Grid gap={16}>
              <TextGroupMedium
                heading={state.isAboutResults ? "Help with your results" : "Help request"}
                body="This will send a message to our support team, who will respond as soon as they can."
              />
              <PopoutInput
                label="Send To"
                renderMain={() => {
                  if (state.sendTo === null) {
                    return null
                  } else if (state.sendTo === "cg") {
                    return (
                      <TextGroupSmall
                        heading="CleverGoose Admin team"
                        body="This will send your request to the CleverGoose admin team, who will respond as soon as they can."
                      />
                    )
                  } else {
                    return state.sendTo.name
                  }
                }}
                renderOptions={ctx => (
                  <div
                    css={css`
                      .option {
                        padding: 10px;
                        cursor: pointer;
                        &:hover {
                          background: #f6f6f6;
                        }

                        p {
                          font-size: 12px;
                          color: #666;
                          margin: 0;
                        }
                      }
                    `}
                  >
                    <div
                      className="option"
                      onClick={() => {
                        mutate.setProp("sendTo", "cg")
                        ctx.popover.hide()
                      }}
                    >
                      <b>CleverGoose Admin team</b>
                      <p>
                        This will send your request to the CleverGoose admin team, who will respond
                        as soon as they can.
                      </p>
                    </div>
                    {state.targetCompanies.map(company => (
                      <div
                        key={company.id}
                        className="option"
                        onClick={() => {
                          mutate.setProp("sendTo", company)
                          ctx.popover.hide()
                        }}
                      >
                        <b>{company.name}</b>
                      </div>
                    ))}
                  </div>
                )}
              />{" "}
              <Fields.TextArea
                label="Message"
                value={state.message}
                onChange={mutate.setter("message")}
                error={state.errors.message}
                rows="auto"
              />
              <MultiFileUploader onFilesUploaded={handleFilesUploaded} uploaderStyle="minimal" />
              {state.isAboutResults && (
                <FieldWithDescription
                  description="I give permission for a member of the the platform team to view my results while investigating this issue."
                  checkbox
                  bordered
                >
                  <Fields.Checkbox
                    value={state.agreedToShareResults}
                    onChange={mutate.setter("agreedToShareResults")}
                    error={state.errors.agreedToShareResults}
                    label="Agree to share results"
                  />
                </FieldWithDescription>
              )}
              <ButtonGrid>
                <SaveButton onClick={handleCreateRequest} />
                <CancelButton onClick={() => onResolve(false)} />
              </ButtonGrid>
            </Grid>
          )}
        </div>
      )
    },
  })
}
